// frontend/src/components/admin/CommunicationsAdmin.jsx
import { useState, useEffect, useCallback } from 'react';
import {
  Bell, Plus, Edit3, Trash2, Eye, EyeOff, X,
  Save, Globe, Building2, CheckCircle2,
  Search, ChevronDown, BarChart2
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import superAdminApi from '../../services/superAdminApi';

// ============================================================================
// CONSTANTES
// ============================================================================

const TYPES = [
  { value: 'nouveaute', label: 'Nouveauté', color: '#3b82f6' },
  { value: 'upsell',    label: 'Upsell',    color: '#8b5cf6' },
  { value: 'alerte',    label: 'Alerte',    color: '#ef4444' },
  { value: 'info',      label: 'Info',      color: '#10b981' },
];

const ICONS = ['Bell', 'Zap', 'Package', 'Clock', 'TrendingUp', 'AlertCircle', 'CheckCircle2', 'Info'];

const GRADIENTS = [
  { label: 'Teal',   value: 'from-[#448696] to-[#6fa3b0]', color: '#448696' },
  { label: 'Bleu',   value: 'from-blue-500 to-indigo-500',  color: '#3b82f6' },
  { label: 'Violet', value: 'from-violet-500 to-purple-600',color: '#8b5cf6' },
  { label: 'Rouge',  value: 'from-red-500 to-rose-600',     color: '#ef4444' },
  { label: 'Vert',   value: 'from-emerald-500 to-teal-500', color: '#10b981' },
  { label: 'Ambre',  value: 'from-amber-500 to-orange-500', color: '#f59e0b' },
];

const DEFAULT_FORM = {
  type: 'info',
  badge: '',
  badge_color: '#448696',
  titre: '',
  contenu: '',
  gradient: 'from-[#448696] to-[#6fa3b0]',
  icon_name: 'Bell',
  is_active: true,
  target_plan: '',
  target_status: '',
  targeting_mode: 'broadcast',
  target_company_ids: [],
};

// ============================================================================
// TYPE BADGE
// ============================================================================

const TypeBadge = ({ type, theme }) => {
  const t = TYPES.find(t => t.value === type) || TYPES[3];
  return (
    <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
      style={{ background: t.color + '18', color: t.color }}>
      {t.label}
    </span>
  );
};

// ============================================================================
// MODE BADGE
// ============================================================================

const ModeBadge = ({ mode, count, theme }) => {
  if (mode === 'broadcast') {
    return (
      <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium"
        style={{ background: theme.primarySoft, color: theme.primary }}>
        <Globe size={10} /> Broadcast
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium"
      style={{ background: theme.warningSoft, color: theme.warningText }}>
      <Building2 size={10} /> {count} entreprise{count > 1 ? 's' : ''}
    </span>
  );
};

// ============================================================================
// SÉLECTEUR CHIPS ENTREPRISES
// ============================================================================

const CompanyChipSelector = ({ selectedIds, onChange, companies, theme }) => {
  const [search, setSearch] = useState('');

  const filtered = companies.filter(c =>
    c.company_name.toLowerCase().includes(search.toLowerCase()) &&
    c.status !== 'inactive'
  );

  const toggle = (id) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter(i => i !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2"
            style={{ color: theme.textMuted }} />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher une entreprise..."
            className="w-full pl-8 pr-3 py-1.5 rounded-lg text-xs"
            style={{ background: theme.bgInput, border: `1px solid ${theme.border}`, color: theme.text }}
          />
        </div>
        <button onClick={() => onChange(filtered.map(c => c.id))}
          className="text-xs px-2 py-1.5 rounded-lg font-medium"
          style={{ background: theme.primarySoft, color: theme.primary }}>
          Tout
        </button>
        <button onClick={() => onChange([])}
          className="text-xs px-2 py-1.5 rounded-lg font-medium"
          style={{ background: theme.bgInput, color: theme.textMuted }}>
          Aucun
        </button>
      </div>

      <div className="flex flex-wrap gap-2 p-3 rounded-xl min-h-16 max-h-40 overflow-y-auto"
        style={{ background: theme.bgInput, border: `1px solid ${theme.border}` }}>
        {filtered.length === 0 && (
          <p className="text-xs w-full text-center py-2" style={{ color: theme.textMuted }}>
            Aucune entreprise
          </p>
        )}
        {filtered.map(c => {
          const selected = selectedIds.includes(c.id);
          return (
            <button
              key={c.id}
              onClick={() => toggle(c.id)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all"
              style={{
                background: selected ? theme.primary : theme.bgCard,
                color:      selected ? '#fff' : theme.textSecondary,
                border:     `1px solid ${selected ? theme.primary : theme.border}`,
                transform:  selected ? 'scale(1.03)' : 'scale(1)',
              }}
            >
              {selected && <CheckCircle2 size={11} />}
              {c.company_name}
            </button>
          );
        })}
      </div>

      {selectedIds.length > 0 && (
        <p className="text-xs" style={{ color: theme.primary }}>
          ✓ {selectedIds.length} entreprise{selectedIds.length > 1 ? 's' : ''} sélectionnée{selectedIds.length > 1 ? 's' : ''}
        </p>
      )}
    </div>
  );
};

// ============================================================================
// MODAL LECTURES
// ============================================================================

const ReadsModal = ({ commId, titre, onClose, theme }) => {
  const [reads, setReads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    superAdminApi.communications.getReads(commId).then(res => {
      if (res.success) setReads(res.data);
      setLoading(false);
    });
  }, [commId]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden"
        style={{ background: theme.bgCard, border: `1px solid ${theme.border}` }}>
        <div className="flex items-center justify-between p-5 border-b"
          style={{ borderColor: theme.border }}>
          <div>
            <p className="font-semibold text-sm" style={{ color: theme.text }}>Lectures</p>
            <p className="text-xs mt-0.5 truncate max-w-xs" style={{ color: theme.textMuted }}>{titre}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg" style={{ color: theme.textMuted }}>
            <X size={18} />
          </button>
        </div>
        <div className="p-5 max-h-96 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-8">
              <span className="animate-spin w-6 h-6 border-2 rounded-full"
                style={{ borderColor: theme.primary, borderTopColor: 'transparent' }} />
            </div>
          ) : reads.length === 0 ? (
            <p className="text-center text-sm py-8" style={{ color: theme.textMuted }}>
              Aucune lecture enregistrée
            </p>
          ) : (
            <div className="space-y-2">
              {reads.map(r => (
                <div key={r.id} className="flex items-center justify-between p-3 rounded-xl"
                  style={{ background: theme.bgInput }}>
                  <div>
                    <p className="text-sm font-medium" style={{ color: theme.text }}>{r.company_name}</p>
                    <p className="text-xs" style={{ color: theme.textMuted }}>{r.user_name} · {r.user_email}</p>
                  </div>
                  <p className="text-xs" style={{ color: theme.textMuted }}>
                    {new Date(r.read_at).toLocaleString('fr-FR', {
                      day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
                    })}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// FORMULAIRE
// ============================================================================

const CommForm = ({ initial, companies, onSave, onCancel, theme }) => {
  const [form, setForm] = useState(initial || { ...DEFAULT_FORM });
  const [saving, setSaving] = useState(false);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSave = async () => {
    if (!form.titre.trim() || !form.contenu.trim()) return;
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  const canSave = form.titre.trim() && form.contenu.trim() &&
    (form.targeting_mode === 'broadcast' || form.target_company_ids.length > 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden"
        style={{ background: theme.bgCard, border: `1px solid ${theme.border}` }}>

        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b"
          style={{ borderColor: theme.border }}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center"
              style={{ background: theme.primarySoft }}>
              <Bell size={18} style={{ color: theme.primary }} />
            </div>
            <p className="font-semibold text-sm" style={{ color: theme.text }}>
              {initial ? 'Modifier la communication' : 'Nouvelle communication'}
            </p>
          </div>
          <button onClick={onCancel} className="p-2 rounded-lg" style={{ color: theme.textMuted }}>
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-5 max-h-[78vh] overflow-y-auto">

          {/* Type + Badge */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold mb-2 block" style={{ color: theme.textSecondary }}>
                Type
              </label>
              <div className="flex gap-2 flex-wrap">
                {TYPES.map(t => (
                  <button key={t.value} onClick={() => set('type', t.value)}
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
                    style={{
                      background: form.type === t.value ? t.color : theme.bgInput,
                      color:      form.type === t.value ? '#fff'  : theme.textSecondary,
                      border:     `1px solid ${form.type === t.value ? t.color : theme.border}`,
                    }}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold mb-2 block" style={{ color: theme.textSecondary }}>
                Badge
              </label>
              <input type="text" value={form.badge}
                onChange={e => set('badge', e.target.value)}
                placeholder="Ex: Nouveauté, Alerte..."
                className="w-full px-3 py-2 rounded-lg text-sm"
                style={{ background: theme.bgInput, border: `1px solid ${theme.border}`, color: theme.text }} />
            </div>
          </div>

          {/* Titre */}
          <div>
            <label className="text-xs font-semibold mb-2 block" style={{ color: theme.textSecondary }}>
              Titre *
            </label>
            <input type="text" value={form.titre}
              onChange={e => set('titre', e.target.value)}
              placeholder="Titre de la communication..."
              className="w-full px-3 py-2 rounded-lg text-sm"
              style={{ background: theme.bgInput, border: `1px solid ${theme.border}`, color: theme.text }} />
          </div>

          {/* Contenu */}
          <div>
            <label className="text-xs font-semibold mb-2 block" style={{ color: theme.textSecondary }}>
              Contenu *
            </label>
            <textarea rows={4} value={form.contenu}
              onChange={e => set('contenu', e.target.value)}
              placeholder="Corps du message..."
              className="w-full px-3 py-2 rounded-lg text-sm resize-none"
              style={{ background: theme.bgInput, border: `1px solid ${theme.border}`, color: theme.text }} />
          </div>

          {/* Couleur + Icône */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold mb-2 block" style={{ color: theme.textSecondary }}>
                Couleur
              </label>
              <div className="flex gap-2 flex-wrap">
                {GRADIENTS.map(g => (
                  <button key={g.value}
                    onClick={() => { set('gradient', g.value); set('badge_color', g.color); }}
                    className="w-7 h-7 rounded-full border-2 transition-transform"
                    style={{
                      background:  g.color,
                      borderColor: form.badge_color === g.color ? theme.text : 'transparent',
                      transform:   form.badge_color === g.color ? 'scale(1.2)' : 'scale(1)',
                    }}
                    title={g.label} />
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold mb-2 block" style={{ color: theme.textSecondary }}>
                Icône
              </label>
              <div className="relative">
                <select value={form.icon_name}
                  onChange={e => set('icon_name', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg text-sm appearance-none"
                  style={{ background: theme.bgInput, border: `1px solid ${theme.border}`, color: theme.text }}>
                  {ICONS.map(i => <option key={i} value={i}>{i}</option>)}
                </select>
                <ChevronDown size={13} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
                  style={{ color: theme.textMuted }} />
              </div>
            </div>
          </div>

          {/* Filtres plan/status (broadcast seulement) */}
          {form.targeting_mode === 'broadcast' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold mb-2 block" style={{ color: theme.textSecondary }}>
                  Filtrer par plan
                </label>
                <select value={form.target_plan}
                  onChange={e => set('target_plan', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg text-sm"
                  style={{ background: theme.bgInput, border: `1px solid ${theme.border}`, color: theme.text }}>
                  <option value="">Tous les plans</option>
                  <option value="basique">Basique</option>
                  <option value="standard">Standard</option>
                  <option value="premium">Premium</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold mb-2 block" style={{ color: theme.textSecondary }}>
                  Filtrer par statut
                </label>
                <select value={form.target_status}
                  onChange={e => set('target_status', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg text-sm"
                  style={{ background: theme.bgInput, border: `1px solid ${theme.border}`, color: theme.text }}>
                  <option value="">Tous les statuts</option>
                  <option value="active">Actif</option>
                  <option value="trial">Essai</option>
                </select>
              </div>
            </div>
          )}

          {/* MODE DE CIBLAGE */}
          <div className="space-y-3">
            <label className="text-xs font-semibold block" style={{ color: theme.textSecondary }}>
              Mode de ciblage
            </label>

            <div className="grid grid-cols-2 gap-3">
              {/* Broadcast */}
              <button
                onClick={() => set('targeting_mode', 'broadcast')}
                className="flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left"
                style={{
                  borderColor: form.targeting_mode === 'broadcast' ? theme.primary : theme.border,
                  background:  form.targeting_mode === 'broadcast' ? theme.primarySofter : theme.bgInput,
                }}>
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: form.targeting_mode === 'broadcast' ? theme.primarySoft : theme.bgCard }}>
                  <Globe size={16} style={{ color: form.targeting_mode === 'broadcast' ? theme.primary : theme.textMuted }} />
                </div>
                <div>
                  <p className="text-xs font-semibold"
                    style={{ color: form.targeting_mode === 'broadcast' ? theme.primary : theme.text }}>
                    Broadcast
                  </p>
                  <p className="text-xs" style={{ color: theme.textMuted }}>Toutes les entreprises</p>
                </div>
              </button>

              {/* Ciblé */}
              <button
                onClick={() => set('targeting_mode', 'targeted')}
                className="flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left"
                style={{
                  borderColor: form.targeting_mode === 'targeted' ? theme.warning : theme.border,
                  background:  form.targeting_mode === 'targeted' ? theme.warningSoft : theme.bgInput,
                }}>
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: form.targeting_mode === 'targeted' ? theme.warningSoft : theme.bgCard }}>
                  <Building2 size={16} style={{ color: form.targeting_mode === 'targeted' ? theme.warningText : theme.textMuted }} />
                </div>
                <div>
                  <p className="text-xs font-semibold"
                    style={{ color: form.targeting_mode === 'targeted' ? theme.warningText : theme.text }}>
                    Ciblé
                  </p>
                  <p className="text-xs" style={{ color: theme.textMuted }}>Entreprises choisies</p>
                </div>
              </button>
            </div>

            {/* Chips sélecteur */}
            {form.targeting_mode === 'targeted' && (
              <div className="p-4 rounded-xl space-y-3"
                style={{ background: theme.warningSoft, border: `1px solid ${theme.warningText}22` }}>
                <div className="flex items-center gap-2">
                  <Building2 size={14} style={{ color: theme.warningText }} />
                  <p className="text-xs font-semibold" style={{ color: theme.warningText }}>
                    Sélectionner les entreprises cibles
                  </p>
                </div>
                <CompanyChipSelector
                  selectedIds={form.target_company_ids}
                  onChange={ids => set('target_company_ids', ids)}
                  companies={companies}
                  theme={theme}
                />
              </div>
            )}
          </div>

          {/* Toggle actif */}
          <div className="flex items-center gap-3">
            <button onClick={() => set('is_active', !form.is_active)}
              className="relative w-11 h-6 rounded-full transition-colors"
              style={{ background: form.is_active ? theme.primary : theme.border }}>
              <span className="absolute top-1 w-4 h-4 rounded-full bg-white transition-all"
                style={{ left: form.is_active ? '1.375rem' : '0.25rem' }} />
            </button>
            <span className="text-sm" style={{ color: theme.textSecondary }}>
              {form.is_active ? 'Communication active' : 'Communication désactivée'}
            </span>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-5 border-t" style={{ borderColor: theme.border }}>
          <button onClick={onCancel}
            className="flex-1 py-2 rounded-xl text-sm font-medium"
            style={{ background: theme.bgInput, color: theme.textSecondary }}>
            Annuler
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !canSave}
            className="flex-1 py-2 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
            style={{ background: theme.primary, color: '#fff' }}>
            {saving
              ? <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
              : <Save size={15} />}
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// LIGNE COMMUNICATION
// ============================================================================

const CommRow = ({ comm, onEdit, onDelete, onToggle, onViewReads, theme }) => (
  <div className="flex items-start gap-4 p-4 rounded-xl transition-colors"
    style={{
      background: theme.bgCard,
      border: `1px solid ${comm.is_active ? theme.border : theme.borderLight}`,
      opacity: comm.is_active ? 1 : 0.6,
    }}>

    <div className="w-1 min-h-12 rounded-full flex-shrink-0 self-stretch"
      style={{ background: comm.badge_color || '#448696' }} />

    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 flex-wrap mb-1">
        <TypeBadge type={comm.type} theme={theme} />
        <ModeBadge
          mode={comm.targeting_mode || 'broadcast'}
          count={comm.target_companies?.length || 0}
          theme={theme}
        />
        {!comm.is_active && (
          <span className="text-xs px-2 py-0.5 rounded-full"
            style={{ background: theme.bgInput, color: theme.textMuted }}>
            Désactivée
          </span>
        )}
      </div>

      <p className="text-sm font-semibold truncate" style={{ color: theme.text }}>
        {comm.titre}
      </p>
      <p className="text-xs mt-0.5 truncate" style={{ color: theme.textMuted }}>
        {comm.contenu?.substring(0, 80)}...
      </p>

      {/* Chips entreprises ciblées */}
      {comm.targeting_mode === 'targeted' && comm.target_companies?.length > 0 && (
        <div className="flex gap-1.5 mt-2 flex-wrap">
          {comm.target_companies.slice(0, 4).map(c => (
            <span key={c.id} className="text-xs px-2 py-0.5 rounded-full"
              style={{ background: theme.warningSoft, color: theme.warningText }}>
              {c.name}
            </span>
          ))}
          {comm.target_companies.length > 4 && (
            <span className="text-xs px-2 py-0.5 rounded-full"
              style={{ background: theme.bgInput, color: theme.textMuted }}>
              +{comm.target_companies.length - 4} autres
            </span>
          )}
        </div>
      )}

      <div className="flex items-center gap-3 mt-2">
        <span className="text-xs" style={{ color: theme.textMuted }}>
          {new Date(comm.created_at).toLocaleDateString('fr-FR')}
        </span>
        <span className="text-xs" style={{ color: theme.textMuted }}>
          {comm.companies_read || 0} lecture(s)
        </span>
      </div>
    </div>

    <div className="flex items-center gap-1 flex-shrink-0">
      <button onClick={() => onViewReads(comm)}
        className="p-2 rounded-lg" style={{ color: theme.textMuted }} title="Lectures">
        <BarChart2 size={15} />
      </button>
      <button onClick={() => onToggle(comm)}
        className="p-2 rounded-lg"
        style={{ color: comm.is_active ? theme.success : theme.textMuted }}
        title={comm.is_active ? 'Désactiver' : 'Activer'}>
        {comm.is_active ? <Eye size={15} /> : <EyeOff size={15} />}
      </button>
      <button onClick={() => onEdit(comm)}
        className="p-2 rounded-lg" style={{ color: theme.primary }} title="Modifier">
        <Edit3 size={15} />
      </button>
      <button onClick={() => onDelete(comm)}
        className="p-2 rounded-lg" style={{ color: theme.danger }} title="Supprimer">
        <Trash2 size={15} />
      </button>
    </div>
  </div>
);

// ============================================================================
// COMPOSANT PRINCIPAL
// ============================================================================

const CommunicationsAdmin = () => {
  const theme = useTheme();
  const [communications, setCommunications] = useState([]);
  const [companies, setCompanies]           = useState([]);
  const [stats, setStats]                   = useState(null);
  const [loading, setLoading]               = useState(true);
  const [modal, setModal]                   = useState(null);
  const [readsModal, setReadsModal]         = useState(null);
  const [filter, setFilter]                 = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [commsRes, statsRes, companiesRes] = await Promise.all([
        superAdminApi.communications.getAll(),
        superAdminApi.communications.getStats(),
        superAdminApi.companies.getAll(),
      ]);
      if (commsRes.success)     setCommunications(commsRes.data);
      if (statsRes.success)     setStats(statsRes.data);
      if (companiesRes.success) setCompanies(companiesRes.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (form) => {
    if (modal === 'new') {
      await superAdminApi.communications.create(form);
    } else {
      await superAdminApi.communications.update(modal.id, form);
    }
    setModal(null);
    load();
  };

  const handleDelete = async (comm) => {
    if (!window.confirm(`Supprimer "${comm.titre}" ?`)) return;
    await superAdminApi.communications.delete(comm.id);
    load();
  };

  const handleToggle = async (comm) => {
    await superAdminApi.communications.toggle(comm.id);
    load();
  };

  const handleEdit = (comm) => {
    setModal({
      ...comm,
      target_company_ids: comm.target_companies?.map(c => c.id) || [],
      targeting_mode: comm.targeting_mode || 'broadcast',
    });
  };

  const filtered = communications.filter(c => {
    if (filter === 'broadcast') return (c.targeting_mode || 'broadcast') === 'broadcast';
    if (filter === 'targeted')  return c.targeting_mode === 'targeted';
    if (filter === 'inactive')  return !c.is_active;
    return true;
  });

  const broadcastCount = communications.filter(c => (c.targeting_mode || 'broadcast') === 'broadcast').length;
  const targetedCount  = communications.filter(c => c.targeting_mode === 'targeted').length;
  const activeCount    = communications.filter(c => c.is_active).length;

  return (
    <>
      {modal && (
        <CommForm
          initial={modal === 'new' ? null : modal}
          companies={companies}
          onSave={handleSave}
          onCancel={() => setModal(null)}
          theme={theme}
        />
      )}

      {readsModal && (
        <ReadsModal
          commId={readsModal.id}
          titre={readsModal.titre}
          onClose={() => setReadsModal(null)}
          theme={theme}
        />
      )}

      <div className="p-6 space-y-6">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold" style={{ color: theme.text }}>Communications</h1>
            <p className="text-sm mt-1" style={{ color: theme.textMuted }}>
              Broadcast vers toutes les entreprises ou ciblé vers des entreprises spécifiques
            </p>
          </div>
          <button onClick={() => setModal('new')}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold"
            style={{ background: theme.primary, color: '#fff' }}>
            <Plus size={16} /> Nouvelle communication
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4">
          {[
            { label: 'Actives',          value: activeCount,    color: theme.primary,     icon: Bell },
            { label: 'Broadcast',        value: broadcastCount, color: theme.success,     icon: Globe },
            { label: 'Ciblées',          value: targetedCount,  color: theme.warning,     icon: Building2 },
            { label: 'Lectures totales', value: stats?.total_reads || 0, color: theme.primary, icon: BarChart2 },
          ].map((s, i) => (
            <div key={i} className="p-4 rounded-xl"
              style={{ background: theme.bgCard, border: `1px solid ${theme.border}` }}>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center"
                  style={{ background: s.color + '18' }}>
                  <s.icon size={18} style={{ color: s.color }} />
                </div>
                <div>
                  <p className="text-xl font-bold" style={{ color: theme.text }}>{s.value}</p>
                  <p className="text-xs" style={{ color: theme.textMuted }}>{s.label}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Filtres */}
        <div className="flex gap-2">
          {[
            { key: 'all',       label: 'Toutes' },
            { key: 'broadcast', label: '🌍 Broadcast' },
            { key: 'targeted',  label: '🎯 Ciblées' },
            { key: 'inactive',  label: 'Désactivées' },
          ].map(f => (
            <button key={f.key} onClick={() => setFilter(f.key)}
              className="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
              style={{
                background: filter === f.key ? theme.primary : theme.bgInput,
                color:      filter === f.key ? '#fff'        : theme.textSecondary,
              }}>
              {f.label}
            </button>
          ))}
        </div>

        {/* Liste */}
        {loading ? (
          <div className="flex justify-center py-12">
            <span className="animate-spin w-8 h-8 border-2 rounded-full"
              style={{ borderColor: theme.primary, borderTopColor: 'transparent' }} />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 rounded-2xl"
            style={{ background: theme.bgCard, border: `1px solid ${theme.border}` }}>
            <Bell size={32} className="mx-auto mb-3" style={{ color: theme.textMuted }} />
            <p className="text-sm" style={{ color: theme.textMuted }}>Aucune communication</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(comm => (
              <CommRow
                key={comm.id}
                comm={comm}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onToggle={handleToggle}
                onViewReads={setReadsModal}
                theme={theme}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default CommunicationsAdmin;